import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, message, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from "../../Images/ArchiveRestore.svg";
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { fetchArchivedCustomerList, unArchiveCustomer } from '../../slices/customer';


function ArchivePlanningList() {
    const [search, setSearch] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]); // Track selected rows
    const [page, setPage] = useState(1);
    const [pagesize, setPageSize] = useState(10);
    const [orderBy, setOrderBy] = useState('');

    const dispatch = useDispatch();
    const { data = [], totalRecord = 0, status } = useSelector(state => state.customer || {});
    useEffect(() => {
        dispatch(fetchArchivedCustomerList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
    }, [dispatch, page, pagesize, search, orderBy]);



    const handleSearch = e => {
        setSearch(e.target.value);
        setPage(1); // Reset to the first page when searching
    };

    const resetFilter = () => {
        setSearch('');
        setPage(1);
        setOrderBy('');
    };

    const handlePageChange = newPage => {
        setPage(newPage);
    };
    const GetSortingEnumValue = (sortField, sortOrder) => {
        let sortingEnumKey = "id";

        if (sortField === "customer_name" && sortOrder === "ascend") {
            sortingEnumKey = "customer_name";
        }
        if (sortField === "customer_name" && sortOrder === "descend") {
            sortingEnumKey = "-customer_name";
        }
        if (sortField === "phone_number" && sortOrder === "ascend") {
            sortingEnumKey = "phone_number";
        }
        if (sortField === "phone_number" && sortOrder === "descend") {
            sortingEnumKey = "-phone_number";
        }


        return sortingEnumKey;
    };
    const handleTableChange = async (pagination, filters, sorter, ordering) => {
        if (sorter && sorter.columnKey && sorter.order) {
            const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
            setOrderBy(orderBy);
        }
        setPageSize(pagination.pageSize);
    };
    const dataSource = data.map((item, index) => ({
        ...item,
        key: item.id || index,
        SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
    }));


    const handleRestore = (id) => {
        if (!id) {
            message.error('Missing ID for deletion!');
            return;
        }

        dispatch(unArchiveCustomer(id))
            .unwrap()
            .then(() => {
                message.success('Customer Unarchived successfully!');
                dispatch(fetchArchivedCustomerList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
            })
            .catch((error) => {
                if (typeof error === 'object' && error !== null) {
                    const messages = [];
                    for (const key in error) {
                        if (Array.isArray(error[key])) {
                            messages.push(`${key}: ${error[key].join(', ')}`);
                        }
                    }

                    if (messages.length > 0) {
                        message.error(messages.join('; '));
                    } else {
                        message.error('An unknown error occurred.');
                    }
                } else if (typeof error === 'string') {
                    message.error(error); // Handle string error
                } else if (error instanceof Error) {
                    message.error(error.message); // Handle Error object
                } else {
                    message.error('An unknown error occurred.');
                }
            });
    };
    const columns = [
        {
            title: 'Sr. No.',
            dataIndex: 'SrNo',
            key: 'SrNo',
            sorter: true,
            width: '8%',
        },
        {
            title: 'Planning Number',
            dataIndex: 'planning_no',
            key: 'planning_no',
            sorter: true,
        },
        {
            title: 'Planning Date',
            dataIndex: 'planning_date',
            key: 'planning_date',
            sorter: true,
        },
        {
            title: 'Plan PCS',
            dataIndex: 'plan_pcs',
            key: 'plan_pcs',
            sorter: true,
        },
        {
            title: 'Plan QTY',
            dataIndex: 'plan_qty',
            key: 'plan_qty',
            sorter: true,
        },
        {
            title: 'Aging Required',
            dataIndex: 'aging_required',
            key: 'aging_required',
        },
        {
            title: 'Work Order No',
            dataIndex: 'work_order_no',
            key: 'work_order_no',
            sorter: true,
        },
        {
            title: 'Work Order Date',
            dataIndex: 'work_order_date',
            key: 'work_order_date',
            sorter: true,
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            sorter: true,
        },
        {
            title: 'Profile No',
            dataIndex: 'die_no',
            key: 'die_no',
            sorter: true,
        },
        {
            title: 'Alloy & Temper',
            dataIndex: 'alloy_temper',
            key: 'alloy_temper',
            sorter: true,
        },
        {
            title: 'Grade',
            dataIndex: 'grade',
            key: 'grade',
            sorter: true,
        },
        {
            title: 'Order PCS',
            dataIndex: 'order_pcs',
            key: 'order_pcs',
            sorter: true,
        },
        {
            title: 'Order QTY',
            dataIndex: 'order_qty',
            key: 'order_qty',
            sorter: true,
        },
        {
            title: 'Archive At',
            dataIndex: 'archive_at',
            key: 'archive_at',
            sorter: true,
            render: (record) => {
                return (
                    <span>{dayjs(record).format('DD-MM-YYYY HH:mm')} </span>
                )
            }
        },
        {
            title: 'Archive By',
            dataIndex: 'archive_by',
            key: 'archive_by',
            sorter: true,
            render: (record) => {
                return (
                    <span>{record ? `${record.first_name}${record.last_name}` : 'N/A'} </span>
                )
            }

        },
        {
            title: 'Action',
            dataIndex: 'action',
            render: (_, record) => (
                <Space>
                    <Tooltip placement="top" title="Restore">
                        <Popconfirm
                            title="Are you sure you want to Unarchive this record?"
                            onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
                            onCancel={() => message.info('Action cancelled')} // Optional cancel action
                            okText="Yes"
                            cancelText="No"
                        >
                            <ArchiveRestore className="ArchiverestorImg" />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];
    const rowSelection = {
        selectedRowKeys,
        onChange: selectedRowKeys => setSelectedRowKeys(selectedRowKeys),
    };

    return (
        <>
            <div className='sticyaction'>
                <Row className="pagenamerow" justify="space-between" align="middle">
                    <Col>

                        <h2>Archived Planning</h2>
                        <div className="bredcrumbwrp">
                            <Link to="/planning-list" className="back">
                                <Back /> BACK
                            </Link>
                            <Breadcrumb
                                items={[
                                    { title: <Link to="/planning-list">Planning</Link> },
                                    { title: 'Archived Planning' }
                                ]}
                            />
                        </div>
                    </Col>
                    <Col>
                        <Space size="small">
                            <Input
                                placeholder="Search"
                  className='searchbox'
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={handleSearch}
                                style={{ width: '100%' }}
                            />
                            <Tooltip placement="top" title={'Reset Filter'}>
                                <Button type="primary" disabled={!search && !orderBy} className='iconlink' onClick={resetFilter}>
                                    <FilterIcon />
                                </Button>
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>
            </div>
            <Table
                rowSelection={rowSelection}
                dataSource={dataSource}
                columns={columns}
                loading={status === 'loading'}
                pagination={{
                    defaultCurrent: page,
                    defaultPageSize: pagesize,
                    total: totalRecord,
                    showTotal: (total) => `Total ${total} items`,
                    showSizeChanger: true,
                    onChange: handlePageChange,
                    locale: {
                        items_per_page: 'Items / Page', // Text for the size changer
                    },
                }}
                onChange={handleTableChange}
                style={{ marginTop: '20px' }}
                scroll={{ x: '600px' }}
            />

        </>
    );
}

export default ArchivePlanningList;

